import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Stack,
  TextField, Backdrop, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, InputAdornment, Divider,
  FormControlLabel, Switch, Grid, MenuItem
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../store/slices/uiSlice';
import apiService from '../services/api.service';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ProjectChooseDialog from '../components/subcontract/ProjectChooseDialog';
import { useSubcontractProjects } from '../hooks/useSubcontractProjects';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

ModuleRegistry.registerModules([AllCommunityModule]);

type SCLine = {
  itemcode: string;
  itemdescription: string;
  quantity: number;
  uomcode: string;
  unitprice: number;
  accountcode: string;
  warehouse: string;
  projectcode: string;
  dim1: string;
  dim2: string;
  dim3: string;
  dim4: string;
  dim5: string;
  linetotal: number;
  lineremarks: string;
};

type SubcontractTypeCode = 'SC' | 'SO' | 'SW';

const SUBCONTRACT_TYPE_OPTIONS: Array<{ code: SubcontractTypeCode; label: string; itemCode: string }> = [
  { code: 'SC', label: 'Subcontract', itemCode: 'SC_V_001' },
  { code: 'SO', label: 'Service Order', itemCode: 'SO_V_001' },
  { code: 'SW', label: 'Sundry work agreement', itemCode: 'SW_V_001' },
];

const normalizeSubcontractTypeCode = (value?: string | null): SubcontractTypeCode => {
  const normalized = String(value || 'SC').trim().toUpperCase();
  if (normalized === 'SO' || normalized === 'SW') return normalized;
  return 'SC';
};

const getDefaultItemCodeForSubcontractType = (type: SubcontractTypeCode): string =>
  SUBCONTRACT_TYPE_OPTIONS.find((option) => option.code === type)?.itemCode || 'SC_V_001';

const SC_DEFAULT_WAREHOUSE = 'SUBCTR';
const normalizeVendorCurrency = (vendor: any): string => {
  const currency = String(
    vendor?.CardCurrency ||
    vendor?.cardcurrency ||
    vendor?.Currency ||
    vendor?.currency ||
    vendor?.CurrCode ||
    vendor?.currcode ||
    vendor?.Data?.CardCurrency ||
    vendor?.Data?.CardCurr ||
    vendor?.Data?.Currency ||
    vendor?.Data?.CurrencyCode ||
    vendor?.Data?.CurrCode ||
    ''
  ).trim().toUpperCase();
  if (!currency || currency === '##' || currency === 'LOCAL' || currency === 'LC') {
    return '';
  }
  return currency;
};

const emptyLine = (itemCode: string = 'SC_V_001'): SCLine => ({
  itemcode: itemCode,
  itemdescription: '',
  quantity: 0,
  uomcode: '',
  unitprice: 1.000,
  accountcode: '',
  warehouse: SC_DEFAULT_WAREHOUSE,
  projectcode: '',
  dim1: '',
  dim2: '',
  dim3: '',
  dim4: '',
  dim5: '',
  linetotal: 0,
  lineremarks: '',
});

const goldButtonSx = {
  bgcolor: '#F1C232',
  color: '#000',
  '&:hover': { bgcolor: '#d4a828' },
  fontWeight: 600,
};

const sectionHeaderSx = {
  fontWeight: 700,
  fontSize: '0.95rem',
  color: '#1a237e',
  mb: 1,
  mt: 2,
};

type SuccessDetails = {
  subcontractNumber: string;
  contractNumber: string;
  projectCode: string;
  projectName: string;
  vendorCode: string;
  vendorName: string;
  contractValue: number;
  currency: string;
  sapDocNum: string;
  sapDocEntry: string;
  postedToSAP: boolean;
};

const isDistinctContractNumber = (subcontractNumber?: string, contractNumber?: string): boolean => {
  const sc = String(subcontractNumber || '').trim();
  const cn = String(contractNumber || '').trim();
  return !!cn && cn !== sc;
};

const CreateSubcontract: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { companyId: selectedCompanyId, projects: projectOptions, loading: projectOptionsLoading } = useSubcontractProjects();
  const [submitting, setSubmitting] = useState(false);
  const [savedSubcontractId, setSavedSubcontractId] = useState<number | null>(null);
  const [savedSubcontractNumber, setSavedSubcontractNumber] = useState('');
  const [savedSapDocNum, setSavedSapDocNum] = useState('');
  const [savedPostedToSAP, setSavedPostedToSAP] = useState(false);
  const [editingSubcontractId, setEditingSubcontractId] = useState<number | null>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successDetails, setSuccessDetails] = useState<SuccessDetails | null>(null);
  const isReadOnly = submitting || (savedSubcontractId !== null && editingSubcontractId === null);
  const isLineSectionReadOnly = true;

  // --- Header fields ---
  const [subcontractType, setSubcontractType] = useState<SubcontractTypeCode>('SC');
  const [projectCode, setProjectCode] = useState('');
  const [projectName, setProjectName] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [contractNumberLoading, setContractNumberLoading] = useState(false);
  const [vendorCode, setVendorCode] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [subcontractCurrency, setSubcontractCurrency] = useState('');
  const [contractStartDate, setContractStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [contractCompletionDate, setContractCompletionDate] = useState('');
  const [subcontractValue, setSubcontractValue] = useState<number>(0);
  const [performanceBondRequired, setPerformanceBondRequired] = useState(false);
  const [performanceBondPct, setPerformanceBondPct] = useState<number>(0);
  const [allRiskInsuranceRequired, setAllRiskInsuranceRequired] = useState(false);
  const [allRiskInsurancePct, setAllRiskInsurancePct] = useState<number>(0);
  const [workmensCompensationRequired, setWorkmensCompensationRequired] = useState(false);
  const [workmensCompensationPct, setWorkmensCompensationPct] = useState<number>(0);
  const [taxClearanceCertRequired, setTaxClearanceCertRequired] = useState(false);
  const [taxClearanceCertPct, setTaxClearanceCertPct] = useState<number>(5);
  const [advancePaymentGuaranteeRequired, setAdvancePaymentGuaranteeRequired] = useState(false);
  const [advancePaymentPct, setAdvancePaymentPct] = useState<number>(0);
  const [retentionPct, setRetentionPct] = useState<number>(0);
  const [retentionGuaranteeRequired, setRetentionGuaranteeRequired] = useState(false);
  const [subcontractAgreementRequired, setSubcontractAgreementRequired] = useState(false);
  const [letterOfAuthorizationRequired, setLetterOfAuthorizationRequired] = useState(false);
  const [civilIDCopiesRequired, setCivilIDCopiesRequired] = useState(false);
  const [bankAccountDetailsRequired, setBankAccountDetailsRequired] = useState(false);

  // --- Line items ---
  const [lines, setLines] = useState<SCLine[]>([emptyLine()]);
  const [currentLineIndex, setCurrentLineIndex] = useState<number>(0);
  const lineGridRef = useRef<any>(null);
  const submitInFlightRef = useRef(false);

  // --- LOV data ---
  const [vendorOptions, setVendorOptions] = useState<any[]>([]);
  const [itemOptions, setItemOptions] = useState<any[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<any[]>([]);
  const [defaultItemMeta, setDefaultItemMeta] = useState<{ itemdescription: string; uomcode: string; dim3: string }>({
    itemdescription: '',
    uomcode: '',
    dim3: '',
  });
  const [vendorOptionsLoading, setVendorOptionsLoading] = useState(false);

  // --- Picker states ---
  const [projectPickerOpen, setProjectPickerOpen] = useState(false);
  const [vendorPickerOpen, setVendorPickerOpen] = useState(false);
  const [vendorSearchQuery, setVendorSearchQuery] = useState('');
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const [itemSearchQuery, setItemSearchQuery] = useState('');
  const [inlinePickerOpen, setInlinePickerOpen] = useState(false);
  const [inlinePickerType, setInlinePickerType] = useState<string>('');
  const [inlinePickerData, setInlinePickerData] = useState<any[]>([]);
  const [inlinePickerColumns, setInlinePickerColumns] = useState<any[]>([]);
  const [inlinePickerTitle, setInlinePickerTitle] = useState('');
  const [inlineSearchQuery, setInlineSearchQuery] = useState('');
  const draftIdFromQuery = useMemo(() => {
    const raw = searchParams.get('draftId') || searchParams.get('subcontractId') || '';
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [searchParams]);
  const defaultItemCode = useMemo(
    () => getDefaultItemCodeForSubcontractType(subcontractType),
    [subcontractType]
  );

  // --- Load LOV data ---
  useEffect(() => {
    let cancelled = false;

    if (!selectedCompanyId) {
      setVendorOptions([]);
      setItemOptions([]);
      setWarehouseOptions([]);
      return () => {
        cancelled = true;
      };
    }

    const loadItems = async () => {
      try {
        const itemRes = await apiService.listLocalItemMaster(
          selectedCompanyId,
          ['ItemCode', 'ItemName', 'ItemsGroupCode', 'InventoryUOM', 'PurchaseUnit', 'ItemType']
        );
        const raw = (itemRes as any)?.data || itemRes || [];
        const normalized = (Array.isArray(raw) ? raw : []).map((it: any) => ({
          ItemCode: it.data?.ItemCode ?? it.ItemCode ?? '',
          ItemName: it.data?.ItemName ?? it.ItemName ?? '',
          ItemsGroupCode: it.data?.ItemsGroupCode ?? it.ItemsGroupCode ?? '',
          InventoryUOM: it.data?.InventoryUOM ?? it.InventoryUOM ?? '',
          PurchaseUnit: it.data?.PurchaseUnit ?? it.PurchaseUnit ?? it.data?.InventoryUOM ?? it.InventoryUOM ?? '',
          ItemType: it.data?.ItemType ?? it.ItemType ?? '',
        })).filter((it: any) => String(it.ItemCode || '').trim());
        if (!cancelled) setItemOptions(normalized);
      } catch (error) {
        if (!cancelled) console.error('Error loading items:', error);
      }
    };

    const loadWarehouses = async () => {
      try {
        const whRes = await apiService.get('/sap/warehouses', { companyId: selectedCompanyId });
        if (!cancelled) setWarehouseOptions((whRes as any)?.data || whRes || []);
      } catch (error) {
        if (!cancelled) console.error('Error loading warehouses:', error);
      }
    };

    void loadItems();
    void loadWarehouses();

    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId]);

  useEffect(() => {
    let cancelled = false;

    const loadDraft = async () => {
      if (!draftIdFromQuery || !selectedCompanyId) return;
      setSubmitting(true);
      try {
        const res = await apiService.getSubcontract(draftIdFromQuery);
        const data = (res as any)?.data || res || null;
        if (!data) {
          throw new Error('Draft subcontract not found');
        }
        if (String(data.status || '').trim().toLowerCase() !== 'draft') {
          dispatch(showSnackbar({ message: 'Selected subcontract is not a draft.', severity: 'warning' }));
          return;
        }
        if (cancelled) return;

        setEditingSubcontractId(Number(data.subcontractid || draftIdFromQuery));
        setSavedSubcontractId(null);
        setSavedSubcontractNumber('');
        setSavedSapDocNum('');
        setSavedPostedToSAP(false);
        setSuccessDialogOpen(false);
        setSubcontractType(normalizeSubcontractTypeCode(data.subcontracttype));
        setProjectCode(String(data.projectcode || ''));
        setProjectName(String(data.projectname || ''));
        setContractNumber(String(data.contractnumber || data.subcontractnumber || ''));
        setVendorCode(String(data.vendorcode || ''));
        setVendorName(String(data.vendorname || ''));
        setSubcontractCurrency(String(data.currency || ''));
        setContractStartDate(String(data.contractstartdate || data.startdate || '').substring(0, 10));
        setContractCompletionDate(String(data.contractcompletiondate || data.enddate || '').substring(0, 10));
        setSubcontractValue(Number(data.contractvalue ?? data.subcontractvalue ?? 0) || 0);
        setPerformanceBondRequired(!!data.performancebondrequired);
        setPerformanceBondPct(Number(data.performancebondpct || 0) || 0);
        setAllRiskInsuranceRequired(!!data.allriskinsurancerequired);
        setAllRiskInsurancePct(Number(data.allriskinsurancepct || 0) || 0);
        setWorkmensCompensationRequired(!!(data.workmenscompensationrequired ?? data.workmenscompinsurancerequired));
        setWorkmensCompensationPct(Number((data.workmenscompensationpct ?? data.workmenscompinsurancepct ?? 0)) || 0);
        setTaxClearanceCertRequired(!!data.taxclearancecertrequired);
        setTaxClearanceCertPct(Number(data.taxclearancecertpct || 0) || 0);
        setAdvancePaymentGuaranteeRequired(!!data.advancepaymentguaranteerequired);
        setAdvancePaymentPct(Number(data.advancepaymentpct || 0) || 0);
        setRetentionPct(Number(data.retentionpct || 0) || 0);
        setRetentionGuaranteeRequired(!!data.retentionguaranteerequired);
        setSubcontractAgreementRequired(!!data.subcontractagreementrequired);
        setLetterOfAuthorizationRequired(!!data.letterofauthorizationrequired);
        setCivilIDCopiesRequired(!!data.civilidcopiesrequired);
        setBankAccountDetailsRequired(!!data.bankaccountdetailsrequired);
        setLines(Array.isArray(data.lines) && data.lines.length > 0
          ? data.lines.map((line: any) => ({
              itemcode: String(line.itemcode || ''),
              itemdescription: String(line.itemdescription || ''),
              quantity: Number(line.quantity || 0) || 0,
              uomcode: String(line.uomcode || ''),
              unitprice: Number(line.unitprice || 0) || 0,
              accountcode: String(line.accountcode || ''),
              warehouse: String(line.warehouse || SC_DEFAULT_WAREHOUSE),
              projectcode: String(line.projectcode || data.projectcode || ''),
              dim1: String(line.dim1 || ''),
              dim2: String(line.dim2 || ''),
              dim3: String(line.dim3 || ''),
              dim4: String(line.dim4 || ''),
              dim5: String(line.dim5 || ''),
              linetotal: Number(line.linetotal || 0) || 0,
              lineremarks: String(line.lineremarks || ''),
            }))
          : [emptyLine(getDefaultItemCodeForSubcontractType(normalizeSubcontractTypeCode(data.subcontracttype)))]);
      } catch (error: any) {
        if (!cancelled) {
          dispatch(showSnackbar({ message: error?.response?.data?.message || error?.message || 'Failed to load draft subcontract', severity: 'error' }));
        }
      } finally {
        if (!cancelled) setSubmitting(false);
      }
    };

    void loadDraft();
    return () => {
      cancelled = true;
    };
  }, [dispatch, draftIdFromQuery, selectedCompanyId]);

  // --- Filtered options for picker dialogs ---
  const filteredVendorOptions = useMemo(() => {
    if (!vendorSearchQuery.trim()) return vendorOptions;
    const q = vendorSearchQuery.toLowerCase();
    return vendorOptions.filter((v: any) =>
      (v.CardCode || v.cardcode || '').toLowerCase().includes(q) ||
      (v.CardName || v.cardname || '').toLowerCase().includes(q)
    );
  }, [vendorOptions, vendorSearchQuery]);

  useEffect(() => {
    let cancelled = false;

    if (!vendorPickerOpen || !selectedCompanyId) {
      setVendorOptionsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    const trimmed = vendorSearchQuery.trim();
    const timeoutId = window.setTimeout(async () => {
      setVendorOptionsLoading(true);
      try {
        const vendorRes = await apiService.get('/sap/vendors', {
          companyId: selectedCompanyId,
          ...(trimmed ? { q: trimmed } : {}),
          limit: 100,
        });
        if (!cancelled) setVendorOptions((vendorRes as any)?.data || vendorRes || []);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading vendors:', error);
          setVendorOptions([]);
        }
      } finally {
        if (!cancelled) setVendorOptionsLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [vendorPickerOpen, vendorSearchQuery, selectedCompanyId]);

  // --- Handlers ---
  const fetchProjectLineDefaults = useCallback(async (projCode: string) => {
    if (!projCode) {
      return { dim1: '', dim2: '' };
    }

    try {
      const res = await apiService.get<any>('/master-data', {
        companyid: selectedCompanyId,
        endpointkey: 'sap-projects',
        keycode: projCode
      });
      const row = Array.isArray((res as any)?.data) ? (res as any).data[0] : undefined;
      const data = row?.Data || row?.data || row?.jsondata || {};
      const defaults = {
        dim1: String(data?.U_Division ?? '').trim(),
        dim2: String(data?.U_Department ?? '').trim(),
      };
      return defaults;
    } catch {
      return { dim1: '', dim2: '' };
    }
  }, [selectedCompanyId]);

  const applyHeaderProjectDefaultsToLines = useCallback((projCode: string, defaults: { dim1: string; dim2: string }) => {
    setLines(prev => prev.map(line => ({
      ...line,
      projectcode: line.projectcode || projCode,
      dim1: defaults.dim1,
      dim2: defaults.dim2,
    })));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadContractNumber = async () => {
      if (!selectedCompanyId || !projectCode || savedSubcontractId || editingSubcontractId) {
        if (!savedSubcontractId && !editingSubcontractId) setContractNumber('');
        return;
      }

      setContractNumberLoading(true);
      try {
        const res = await apiService.getNextSubcontractContractNumber(projectCode, contractStartDate, selectedCompanyId, subcontractType);
        const nextContractNumber = String(
          (res as any)?.data?.contractnumber ||
          (res as any)?.contractnumber ||
          ''
        );
        if (!cancelled) setContractNumber(nextContractNumber);
      } catch (error) {
        if (!cancelled) {
          console.error('Error loading subcontract contract number:', error);
          setContractNumber('');
        }
      } finally {
        if (!cancelled) setContractNumberLoading(false);
      }
    };

    void loadContractNumber();
    return () => {
      cancelled = true;
    };
  }, [contractStartDate, editingSubcontractId, projectCode, savedSubcontractId, selectedCompanyId, subcontractType]);

  const resolveItemGroupCostCode = useCallback(async (itemGroupCode: string): Promise<string> => {
    const code = String(itemGroupCode || '').trim();
    if (!code) return '';

    try {
      const groupRes = await apiService.getItemGroups({ companyId: selectedCompanyId, code });
      const groups = (groupRes as any)?.data || groupRes || [];
      const group = Array.isArray(groups) ? groups[0] : undefined;
      const costCode = String(
        group?.Data?.U_COST_CODE ??
        group?.U_COST_CODE ??
        group?.Data?.U_MCSR_CODE ??
        group?.U_MCSR_CODE ??
        ''
      ).trim();
      if (!costCode) return '';

      const drRes = await apiService.get<any>('/master-data/distribution-rules', {
        dimension: 3,
        companyid: selectedCompanyId
      });
      const rules = (drRes as any)?.data || drRes || [];
      const exists = Array.isArray(rules) && rules.some((r: any) =>
        String(r.FactorCode || r.Code || r.DistributionRule || '').trim() === costCode
      );
      return exists ? costCode : costCode;
    } catch {
      return '';
    }
  }, [selectedCompanyId]);

  const applyDefaultItemMetaToLine = useCallback((line: SCLine): SCLine => {
    const next: SCLine = {
      ...line,
      itemcode: defaultItemCode,
    };
    if (!String(next.itemdescription || '').trim() && defaultItemMeta.itemdescription) {
      next.itemdescription = defaultItemMeta.itemdescription;
    }
    if (!String(next.uomcode || '').trim() && defaultItemMeta.uomcode) {
      next.uomcode = defaultItemMeta.uomcode;
    }
    if (!String(next.dim3 || '').trim() && defaultItemMeta.dim3) {
      next.dim3 = defaultItemMeta.dim3;
    }
    return next;
  }, [defaultItemCode, defaultItemMeta]);

  // --- Pre-fill default item on subcontract lines ---
  useEffect(() => {
    const match = itemOptions.find((i: any) =>
      (i.ItemCode || i.itemcode || '') === defaultItemCode
    );
    if (!match) {
      setDefaultItemMeta({ itemdescription: '', uomcode: '', dim3: '' });
      setLines((prev) => prev.map((line) => ({ ...line, itemcode: defaultItemCode })));
      return;
    }

    void (async () => {
      const itemGroupCode = String(match.ItemsGroupCode || match.itemsgroupcode || '').trim();
      const costCode = itemGroupCode ? await resolveItemGroupCostCode(itemGroupCode) : '';
      const nextMeta = {
        itemdescription: match.ItemName || match.itemname || match.ItemDescription || match.itemdescription || '',
        uomcode: match.InventoryUOM || match.uomcode || '',
        dim3: costCode || '',
      };
      setDefaultItemMeta(nextMeta);
      setLines(prev => prev.map((line) => {
        const next = { ...line, itemcode: defaultItemCode };
        if (!String(next.itemdescription || '').trim() && nextMeta.itemdescription) next.itemdescription = nextMeta.itemdescription;
        if (!String(next.uomcode || '').trim() && nextMeta.uomcode) next.uomcode = nextMeta.uomcode;
        if (!String(next.dim3 || '').trim() && nextMeta.dim3) next.dim3 = nextMeta.dim3;
        return next;
      }));
    })();
  }, [defaultItemCode, itemOptions, resolveItemGroupCostCode]);

  const handleProjectSelect = useCallback(async (proj: any) => {
    const code = proj.Code || proj.code || '';
    setProjectCode(code);
    setProjectName(proj.Name || proj.name || '');
    setProjectPickerOpen(false);

    const defaults = await fetchProjectLineDefaults(code);
    applyHeaderProjectDefaultsToLines(code, defaults);
  }, [applyHeaderProjectDefaultsToLines, fetchProjectLineDefaults]);

  const handleVendorSelect = useCallback((vendor: any) => {
    if (isReadOnly) return;
    setVendorCode(vendor.CardCode || vendor.cardcode || '');
    setVendorName(vendor.CardName || vendor.cardname || '');
    setSubcontractCurrency(normalizeVendorCurrency(vendor));
    setVendorPickerOpen(false);
    setVendorSearchQuery('');
  }, [isReadOnly]);

  // --- Line item helpers ---
  const updateLine = useCallback((index: number, updates: Partial<SCLine>) => {
    if (isReadOnly) return;
    setLines(prev => {
      const copy = [...prev];
      copy[index] = {
        ...copy[index],
        ...updates,
        itemcode: defaultItemCode,
      };
      // Recalc line total: quantity * unitprice
      const q = updates.quantity !== undefined ? updates.quantity : copy[index].quantity;
      const p = updates.unitprice !== undefined ? updates.unitprice : copy[index].unitprice;
      copy[index].linetotal = Number((q * p).toFixed(3));
      return copy;
    });
  }, [defaultItemCode, isReadOnly]);

  // When subcontractValue changes, update the first line quantity = value, unitprice stays 1
  useEffect(() => {
    setLines(prev => {
      const copy = [...prev];
    if (copy.length > 0) {
        copy[0] = { ...applyDefaultItemMetaToLine(copy[0]), quantity: Number(subcontractValue) || 0, unitprice: 1.000, linetotal: Number(subcontractValue) || 0, itemcode: defaultItemCode };
      }
      return copy;
    });
  }, [applyDefaultItemMetaToLine, defaultItemCode, subcontractValue]);

  const handleItemSelect = useCallback(async (item: any) => {
    const itemGroupCode = String(item.ItemsGroupCode || item.itemsgroupcode || '').trim();
    const costCode = itemGroupCode ? await resolveItemGroupCostCode(itemGroupCode) : '';

    updateLine(currentLineIndex, {
      itemcode: defaultItemCode,
      itemdescription: item.ItemName || item.itemname || '',
      uomcode: item.PurchaseUnit || item.purchaseunit || '',
      unitprice: Number(item.Price || item.price || 1),
      ...(costCode ? { dim3: costCode } : {}),
    });
    setItemPickerOpen(false);
    setItemSearchQuery('');
  }, [currentLineIndex, defaultItemCode, resolveItemGroupCostCode, updateLine]);

  const chooseInlineValue = useCallback((row: any) => {
    if (isReadOnly) return;
    const fieldMap: Record<string, string> = {
      warehouse: row.WarehouseCode || row.warehousecode || row.Code || row.code || '',
      projectcode: row.Code || row.code || '',
      dim1: row.FactorCode || row.Code || row.code || '',
      dim2: row.FactorCode || row.Code || row.code || '',
      dim3: row.FactorCode || row.Code || row.code || '',
      dim4: row.FactorCode || row.Code || row.code || '',
      dim5: row.FactorCode || row.Code || row.code || '',
    };
    const value = fieldMap[inlinePickerType] || '';
    updateLine(currentLineIndex, { [inlinePickerType]: value } as any);
    setInlinePickerOpen(false);
    setInlineSearchQuery('');
  }, [currentLineIndex, inlinePickerType, isReadOnly, updateLine]);

  // --- Filtered inline picker data ---
  const filteredInlineData = useMemo(() => {
    if (!inlineSearchQuery.trim()) return inlinePickerData;
    const q = inlineSearchQuery.toLowerCase();
    return inlinePickerData.filter((r: any) =>
      Object.values(r).some(v => String(v || '').toLowerCase().includes(q))
    );
  }, [inlinePickerData, inlineSearchQuery]);

  const filteredItemOptions = useMemo(() => {
    if (!itemSearchQuery.trim()) return itemOptions;
    const q = itemSearchQuery.toLowerCase();
    return itemOptions.filter((i: any) =>
      (i.ItemCode || i.itemcode || '').toLowerCase().includes(q) ||
      (i.ItemName || i.itemname || '').toLowerCase().includes(q)
    );
  }, [itemOptions, itemSearchQuery]);

  // AG-Grid line column defs
  const lineColumnDefs = useMemo(() => [
    { headerName: '#', valueGetter: (p: any) => (p.node?.rowIndex ?? 0) + 1, width: 55, pinned: 'left', editable: false, checkboxSelection: false, headerCheckboxSelection: false } as any,
    { field: 'itemcode', headerName: 'Item Code', minWidth: 130, editable: false, cellStyle: { fontWeight: 600, backgroundColor: '#f5f5f5' } },
    { field: 'itemdescription', headerName: 'Description', minWidth: 220, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'quantity', headerName: 'Qty', minWidth: 90, editable: false, type: 'numericColumn', cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'uomcode', headerName: 'UoM', minWidth: 80, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'unitprice', headerName: 'Unit Price', minWidth: 110, editable: false, type: 'numericColumn', valueFormatter: (p: any) => Number(p.value || 0).toFixed(3), cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'linetotal', headerName: 'Line Total', minWidth: 120, editable: false, type: 'numericColumn', valueFormatter: (p: any) => Number(p.value || 0).toFixed(3), cellStyle: { fontWeight: 600 } },
    { field: 'accountcode', headerName: 'Account Code', minWidth: 130, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'warehouse', headerName: 'Warehouse', minWidth: 120, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'projectcode', headerName: 'Project Code', minWidth: 130, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'dim1', headerName: 'Division', minWidth: 110, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'dim2', headerName: 'Department', minWidth: 130, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'dim3', headerName: 'Cost Code (WBS)', minWidth: 150, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'dim4', headerName: 'Auxiliary', minWidth: 120, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'dim5', headerName: 'LOC', minWidth: 100, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
    { field: 'lineremarks', headerName: 'Remarks', minWidth: 200, editable: false, cellStyle: { backgroundColor: '#f5f5f5' } },
  ], []);

  const onLineCellValueChanged = useCallback((event: any) => {
    if (isReadOnly || isLineSectionReadOnly) return;
    const { colDef, data, rowIndex } = event;
    if (rowIndex == null) return;
    if (colDef.field === 'quantity' || colDef.field === 'unitprice') {
      const q = Number(data.quantity) || 0;
      const p = Number(data.unitprice) || 0;
      updateLine(rowIndex, { quantity: q, unitprice: p });
    }
  }, [isLineSectionReadOnly, isReadOnly, updateLine]);

  const onLineCellDoubleClicked = useCallback(async (event: any) => {
    if (isReadOnly || isLineSectionReadOnly) return;
    const field = event.colDef?.field;
    const idx = event.rowIndex ?? 0;
    setCurrentLineIndex(idx);

    if (field === 'warehouse') {
      setInlinePickerType('warehouse');
      setInlinePickerTitle('Select Warehouse');
      setInlinePickerColumns([{ field: 'WarehouseCode', headerName: 'Code', flex: 1 }, { field: 'WarehouseName', headerName: 'Name', flex: 2 }]);
      setInlinePickerData(warehouseOptions);
      setInlinePickerOpen(true);
      return;
    }
    if (field === 'projectcode') {
      setInlinePickerType('projectcode');
      setInlinePickerTitle('Select Project');
      setInlinePickerColumns([{ field: 'Code', headerName: 'Code', flex: 1 }, { field: 'Name', headerName: 'Name', flex: 2 }]);
      setInlinePickerData(projectOptions);
      setInlinePickerOpen(true);
      return;
    }
    if (['dim1', 'dim2', 'dim3', 'dim4', 'dim5'].includes(field || '')) {
      setInlinePickerType(field || '');
      const dimLabels: Record<string, string> = { dim1: 'Division', dim2: 'Department', dim3: 'Cost Code (WBS)', dim4: 'Auxiliary', dim5: 'LOC' };
      setInlinePickerTitle(`Select ${dimLabels[field || 'dim1']}`);
      setInlinePickerColumns([{ field: 'FactorCode', headerName: 'Code', flex: 1 }, { field: 'FactorDescription', headerName: 'Description', flex: 2 }]);

      try {
        let data: any[] = [];
        if (field === 'dim4') {
          const row = lines[idx];
          const rowProjectCode = String(row?.projectcode || '').trim();
          if (rowProjectCode) {
            const res = await apiService.get<any>('/master-data/auxiliary-by-project', {
              projectcode: rowProjectCode,
              companyid: selectedCompanyId
            });
            data = (res as any)?.data || res || [];
          } else {
            const res = await apiService.get<any>('/master-data/distribution-rules', {
              dimension: 5,
              companyid: selectedCompanyId
            });
            data = (res as any)?.data || res || [];
          }
        } else {
          const dimensionMap: Record<string, number> = {
            dim1: 1,
            dim2: 2,
            dim3: 3,
            dim5: 4,
          };
          const res = await apiService.get<any>('/master-data/distribution-rules', {
            dimension: dimensionMap[field || 'dim1'],
            companyid: selectedCompanyId
          });
          data = (res as any)?.data || res || [];
        }
        setInlinePickerData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error loading dimension picker data:', error);
        setInlinePickerData([]);
      }

      setInlinePickerOpen(true);
      return;
    }
  }, [isLineSectionReadOnly, isReadOnly, lines, projectOptions, selectedCompanyId, warehouseOptions]);

  const onLineCellClicked = useCallback((event: any) => {
    if (isReadOnly || isLineSectionReadOnly) return;
    const field = String(event.colDef?.field || '');
    if (!['projectcode', 'dim1', 'dim2', 'dim3', 'dim4', 'dim5'].includes(field)) return;
    onLineCellDoubleClicked(event);
  }, [isLineSectionReadOnly, isReadOnly, onLineCellDoubleClicked]);

  // Compute total from lines
  const linesTotal = useMemo(() => lines.reduce((sum, l) => sum + (l.linetotal || 0), 0), [lines]);

  // --- Validation ---
  const validate = useCallback((): string[] => {
    const errors: string[] = [];
    if (!projectCode) errors.push('Project is required.');
    if (!selectedCompanyId) errors.push('Company selection is required.');
    if (!vendorCode) errors.push('Vendor / Subcontractor is required.');
    if (!contractStartDate) errors.push('Contract Start Date is required.');
    if (!contractCompletionDate) errors.push('Contract Completion Date is required.');
    if (contractStartDate && contractCompletionDate && contractCompletionDate <= contractStartDate) {
      errors.push('Contract Completion Date must be after Contract Start Date.');
    }
    if ((subcontractValue || 0) <= 0) errors.push('Subcontract Value must be greater than 0.');
    if (performanceBondRequired && (performanceBondPct || 0) <= 0) {
      errors.push('Performance Bond % is required when Performance Bond is enabled.');
    }
    if (allRiskInsuranceRequired && (allRiskInsurancePct || 0) <= 0) {
      errors.push('All Risk Insurance % is required when All Risk Insurance is enabled.');
    }
    if (workmensCompensationRequired && (workmensCompensationPct || 0) <= 0) {
      errors.push('Workmen\'s Compensation % is required when Workmen\'s Compensation Insurance is enabled.');
    }
    if (taxClearanceCertRequired && (taxClearanceCertPct || 0) <= 0) {
      errors.push('Tax Clearance Certificate % is required when Tax Clearance Certificate is enabled.');
    }
    if (advancePaymentGuaranteeRequired && (advancePaymentPct || 0) <= 0) {
      errors.push('Advance Payment % is required when Advance Payment Guarantee is enabled.');
    }
    return errors;
  }, [
    projectCode, vendorCode, contractStartDate, contractCompletionDate, subcontractValue,
    performanceBondRequired, performanceBondPct,
    allRiskInsuranceRequired, allRiskInsurancePct,
    workmensCompensationRequired, workmensCompensationPct,
    taxClearanceCertRequired, taxClearanceCertPct,
    advancePaymentGuaranteeRequired, advancePaymentPct,
    selectedCompanyId,
  ]);

  // --- Submit ---
  const handleSave = async (postToSAP = false) => {
    if (submitInFlightRef.current) return;
    const errors = validate();
    if (errors.length > 0) {
      dispatch(showSnackbar({ message: errors.join(' '), severity: 'error' }));
      return;
    }

    submitInFlightRef.current = true;
    setSubmitting(true);
    try {
      const selectedCompanyId = Number(localStorage.getItem('selectedCompanyId') || '') || undefined;
      const payload = {
        subcontracttype: subcontractType,
        projectcode: projectCode,
        projectname: projectName,
        contractnumber: contractNumber,
        vendorcode: vendorCode,
        vendorname: vendorName,
        contractstartdate: contractStartDate,
        contractcompletiondate: contractCompletionDate,
        subcontractvalue: Number(subcontractValue) || 0,
        subcontractcurrency: subcontractCurrency || undefined,
        performancebondrequired: performanceBondRequired,
        performancebondpct: performanceBondRequired ? Number(performanceBondPct) || 0 : 0,
        allriskinsurancerequired: allRiskInsuranceRequired,
        allriskinsurancepct: allRiskInsuranceRequired ? Number(allRiskInsurancePct) || 0 : 0,
        workmenscompensationrequired: workmensCompensationRequired,
        workmenscompensationpct: workmensCompensationRequired ? Number(workmensCompensationPct) || 0 : 0,
        taxclearancecertrequired: taxClearanceCertRequired,
        taxclearancecertpct: taxClearanceCertRequired ? Number(taxClearanceCertPct) || 0 : 0,
        advancepaymentguaranteerequired: advancePaymentGuaranteeRequired,
        advancepaymentpct: advancePaymentGuaranteeRequired ? Number(advancePaymentPct) || 0 : 0,
        retentionpct: Number(retentionPct) || 0,
        retentionguaranteerequired: retentionGuaranteeRequired,
        subcontractagreementrequired: subcontractAgreementRequired,
        letterofauthorizationrequired: letterOfAuthorizationRequired,
        civilidcopiesrequired: civilIDCopiesRequired,
        bankaccountdetailsrequired: bankAccountDetailsRequired,
        companyid: selectedCompanyId,
        lines: lines.filter(l => l.itemcode || l.itemdescription).map((l, i) => ({
          linenum: i + 1,
          itemcode: l.itemcode,
          itemdescription: l.itemdescription,
          quantity: Number(l.quantity) || 0,
          uomcode: l.uomcode,
          unitprice: Number(l.unitprice) || 1,
          linetotal: Number(l.linetotal) || 0,
          accountcode: l.accountcode,
          warehouse: l.warehouse || SC_DEFAULT_WAREHOUSE,
          projectcode: l.projectcode,
          dim1: l.dim1,
          dim2: l.dim2,
          dim3: l.dim3,
          dim4: l.dim4,
          dim5: l.dim5,
          lineremarks: l.lineremarks,
        })),
      };

      let data: any;
      let sapDocNum: any;
      let sapDocEntry: any;
      let postedToSAP = false;

      if (editingSubcontractId) {
        const updateResponse: any = await apiService.updateSubcontract(editingSubcontractId, payload);
        data = updateResponse?.data || updateResponse;
        if (postToSAP) {
          const postResponse: any = await apiService.postSubcontractToSAP(editingSubcontractId);
          data = postResponse?.data || postResponse;
          sapDocNum = data?.sappodocnum || data?.sapPosting?.docNum;
          sapDocEntry = data?.sappodocentry || data?.sapPosting?.docEntry;
          postedToSAP = !!(sapDocNum || sapDocEntry || String(data?.sappoststatus || '').toLowerCase() === 'posted');
          setEditingSubcontractId(null);
          setSavedSubcontractId(editingSubcontractId);
          setSavedSubcontractNumber(String(data?.subcontractnumber || data?.SubcontractNumber || ''));
          setSavedSapDocNum(String(sapDocNum || ''));
          setSavedPostedToSAP(postedToSAP);
          setSuccessDetails({
            subcontractNumber: String(data?.subcontractnumber || data?.SubcontractNumber || ''),
            contractNumber: String(data?.contractnumber || data?.ContractNumber || contractNumber || ''),
            projectCode,
            projectName,
            vendorCode,
            vendorName,
            contractValue: Number(subcontractValue) || 0,
            currency: String(subcontractCurrency || ''),
            sapDocNum: String(sapDocNum || ''),
            sapDocEntry: String(sapDocEntry || ''),
            postedToSAP,
          });
          setSuccessDialogOpen(true);
        } else {
          dispatch(showSnackbar({ message: 'Subcontract draft updated successfully', severity: 'success' }));
        }
      } else {
        const response: any = await apiService.createSubcontract(payload, { postToSAP });
        data = response?.data || response;
        sapDocNum = data?.sappodocnum || data?.sapPosting?.docNum;
        sapDocEntry = data?.sappodocentry || data?.sapPosting?.docEntry;
        postedToSAP = !!(sapDocNum || sapDocEntry || String(data?.sappoststatus || '').toLowerCase() === 'posted');
        setSavedSubcontractId(Number(data?.subcontractid || data?.SubcontractID || 0) || -1);
        setSavedSubcontractNumber(String(data?.subcontractnumber || data?.SubcontractNumber || ''));
        setContractNumber(String(data?.contractnumber || data?.ContractNumber || contractNumber || ''));
        setSavedSapDocNum(String(sapDocNum || ''));
        setSavedPostedToSAP(postedToSAP);
        setSuccessDetails({
          subcontractNumber: String(data?.subcontractnumber || data?.SubcontractNumber || ''),
          contractNumber: String(data?.contractnumber || data?.ContractNumber || contractNumber || ''),
          projectCode,
          projectName,
          vendorCode,
          vendorName,
          contractValue: Number(subcontractValue) || 0,
          currency: String(subcontractCurrency || ''),
          sapDocNum: String(sapDocNum || ''),
          sapDocEntry: String(sapDocEntry || ''),
          postedToSAP,
        });
        setSuccessDialogOpen(true);
      }

      setProjectPickerOpen(false);
      setVendorPickerOpen(false);
      setItemPickerOpen(false);
      setInlinePickerOpen(false);
    } catch (error: any) {
      const errorBody = error?.response?.data || {};
      const savedDraft = errorBody?.data || null;
      const savedDraftId = Number(savedDraft?.subcontractid || savedDraft?.SubcontractID || 0);
      if (!editingSubcontractId && savedDraftId > 0) {
        const savedNumber = String(savedDraft?.subcontractnumber || savedDraft?.SubcontractNumber || contractNumber || '');
        setEditingSubcontractId(savedDraftId);
        setSavedSubcontractId(null);
        setSavedSubcontractNumber(savedNumber);
        setContractNumber(savedNumber);
        setSavedSapDocNum('');
        setSavedPostedToSAP(false);
        navigate(`/subcontract/create?draftId=${savedDraftId}`, { replace: true });
      }
      const msg = errorBody?.message || error?.message || 'Failed to create subcontract';
      dispatch(showSnackbar({ message: msg, severity: 'error' }));
    } finally {
      submitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Paper component="section" aria-labelledby="cs-heading" elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <Backdrop open={submitting} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Dialog
        open={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.5, pt: 2.5, textAlign: 'center' }}>
          <Stack spacing={1} alignItems="center">
            <CheckCircleOutlineIcon sx={{ fontSize: 42, color: '#2e7d32' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {successDetails?.postedToSAP ? 'Subcontract Created Successfully' : 'Subcontract Draft Saved'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {successDetails?.postedToSAP
                ? 'The subcontract was saved locally and posted to SAP.'
                : 'The subcontract was saved locally as a draft.'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 2 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              borderColor: '#d9e2ec',
              background: 'linear-gradient(180deg, #ffffff 0%, #f9fbfd 100%)',
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Subcontract Number</Typography>
                <Typography sx={{ fontWeight: 700 }}>{successDetails?.subcontractNumber || '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">SAP PO DocNum</Typography>
                <Typography sx={{ fontWeight: 700 }}>{successDetails?.sapDocNum || '-'}</Typography>
              </Grid>
              {isDistinctContractNumber(successDetails?.subcontractNumber, successDetails?.contractNumber) && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Contract Number</Typography>
                  <Typography>{successDetails?.contractNumber || '-'}</Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">SAP PO DocEntry</Typography>
                <Typography>{successDetails?.sapDocEntry || '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Project</Typography>
                <Typography>{successDetails?.projectCode ? `${successDetails.projectCode} - ${successDetails.projectName}` : '-'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Vendor</Typography>
                <Typography>{successDetails?.vendorCode ? `${successDetails.vendorCode} - ${successDetails.vendorName}` : '-'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Contract Value</Typography>
                <Typography>{Number(successDetails?.contractValue || 0).toFixed(3)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Currency</Typography>
                <Typography>{successDetails?.currency || 'SAP Default'}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'center' }}>
          <Button onClick={() => setSuccessDialogOpen(false)} sx={goldButtonSx}>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Page Title & Action Buttons */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={{ xs: 1, sm: 0 }}
        mb={2}
      >
        <Typography
          component="h1"
          id="cs-heading"
          variant="h5"
          sx={{ fontSize: { xs: 18, sm: 22, md: 24 }, fontWeight: 700 }}
        >
          {savedSubcontractId ? 'Subcontract Saved' : (editingSubcontractId ? 'Edit Subcontract Draft' : 'Create Subcontract')}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant="contained"
            onClick={() => handleSave(false)}
            disabled={isReadOnly}
            sx={goldButtonSx}
          >
            {savedSubcontractId ? 'Saved' : 'Save Draft'}
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleCancel}
            disabled={submitting}
          >
            {savedSubcontractId ? 'Close' : 'Cancel'}
          </Button>
        </Stack>
      </Stack>

      {savedSubcontractId ? (
        <Box sx={{ mb: 2 }}>
          {savedSubcontractNumber ? (
            <Typography sx={{ fontWeight: 700, color: '#1a237e' }}>
              Subcontract Number: {savedSubcontractNumber}
            </Typography>
          ) : null}
          {savedSapDocNum ? (
            <Typography sx={{ mt: 0.5, color: '#455a64' }}>
              SAP PO DocNum: {savedSapDocNum}
            </Typography>
          ) : null}
          {!savedPostedToSAP ? (
            <Typography sx={{ mt: 0.5, color: '#ef6c00' }}>
              Status: Draft
            </Typography>
          ) : null}
        </Box>
      ) : null}

      <fieldset disabled={isReadOnly} style={{ border: 0, padding: 0, margin: 0, minInlineSize: 0 }}>

      {/* ===== HEADER BLOCK - Project & Vendor ===== */}
      <Typography sx={sectionHeaderSx}>Project &amp; Vendor Details</Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Type */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            size="small"
            label="Type"
            value={subcontractType}
            onChange={(e) => setSubcontractType(e.target.value as SubcontractTypeCode)}
            fullWidth
          >
            {SUBCONTRACT_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.code} value={option.code}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Project Number */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            label="Project Number"
            value={projectCode ? `${projectCode} - ${projectName}` : ''}
            onClick={() => { if (!isReadOnly) setProjectPickerOpen(true); }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" sx={{ color: '#87CEEB', cursor: 'pointer' }} />
                </InputAdornment>
              ),
            }}
            fullWidth
            required
            sx={{ cursor: 'pointer' }}
          />
        </Grid>

        {/* Project Name */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            label="Project Name"
            value={projectName}
            InputProps={{ readOnly: true }}
            fullWidth
            sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }}
          />
        </Grid>

        {/* Contract Number */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            label="Contract Number"
            value={contractNumber}
            InputProps={{ readOnly: true }}
            fullWidth
            helperText={projectCode ? `Auto-generated in IDS/${subcontractType}/Project/Serial/Year format` : 'Select a project to generate the contract number'}
            sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }}
            disabled={contractNumberLoading}
          />
        </Grid>

        {/* Vendor / Subcontractor */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            label="Vendor / Subcontractor"
            value={vendorCode ? `${vendorCode} - ${vendorName}` : ''}
            onClick={() => { if (!isReadOnly) setVendorPickerOpen(true); }}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon fontSize="small" sx={{ color: '#87CEEB', cursor: 'pointer' }} />
                </InputAdornment>
              ),
            }}
            fullWidth
            required
            sx={{ cursor: 'pointer' }}
          />
        </Grid>

        {/* Subcontract Currency */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            label="Subcontract Currency"
            value={subcontractCurrency || 'BP / SAP Default'}
            InputProps={{ readOnly: true }}
            fullWidth
            sx={{ '& .MuiInputBase-input': { bgcolor: '#f5f5f5' } }}
          />
        </Grid>
      </Grid>

      {/* ===== HEADER BLOCK - Contract Dates & Value ===== */}
      <Typography sx={sectionHeaderSx}>Contract Dates &amp; Value</Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Contract Start Date */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="date"
            label="Contract Start Date"
            value={contractStartDate}
            onChange={(e) => setContractStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
        </Grid>

        {/* Contract Completion Date */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="date"
            label="Contract Completion Date"
            value={contractCompletionDate}
            onChange={(e) => setContractCompletionDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            required
          />
        </Grid>

        {/* Subcontract Value */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="number"
            label={subcontractCurrency ? `Subcontract Value (${subcontractCurrency})` : 'Subcontract Value'}
            value={subcontractValue || ''}
            onChange={(e) => setSubcontractValue(Number(e.target.value) || 0)}
            inputProps={{ step: '0.001', min: 0 }}
            InputProps={{
              endAdornment: subcontractCurrency ? <InputAdornment position="end">{subcontractCurrency}</InputAdornment> : undefined,
            }}
            fullWidth
            required
          />
        </Grid>
      </Grid>

      {/* ===== HEADER BLOCK - Bond, Insurance & Guarantees ===== */}
      <Typography sx={sectionHeaderSx}>Bond, Insurance &amp; Guarantees</Typography>
      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {/* Performance Bond */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={performanceBondRequired}
                onChange={(e) => {
                  setPerformanceBondRequired(e.target.checked);
                  if (!e.target.checked) setPerformanceBondPct(0);
                }}
              />
            }
            label="Performance Bond Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="number"
            label="Performance Bond %"
            value={performanceBondPct || ''}
            onChange={(e) => setPerformanceBondPct(Number(e.target.value) || 0)}
            inputProps={{ step: '0.01', min: 0, max: 100 }}
            disabled={!performanceBondRequired}
            fullWidth
            required={performanceBondRequired}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} />

        {/* All Risk Insurance */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={allRiskInsuranceRequired}
                onChange={(e) => {
                  setAllRiskInsuranceRequired(e.target.checked);
                  if (!e.target.checked) setAllRiskInsurancePct(0);
                }}
              />
            }
            label="All Risk Insurance Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="number"
            label="All Risk Insurance %"
            value={allRiskInsurancePct || ''}
            onChange={(e) => setAllRiskInsurancePct(Number(e.target.value) || 0)}
            inputProps={{ step: '0.01', min: 0, max: 100 }}
            disabled={!allRiskInsuranceRequired}
            fullWidth
            required={allRiskInsuranceRequired}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} />

        {/* Workmen's Compensation Insurance */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={workmensCompensationRequired}
                onChange={(e) => {
                  setWorkmensCompensationRequired(e.target.checked);
                  if (!e.target.checked) setWorkmensCompensationPct(0);
                }}
              />
            }
            label="Workmen's Compensation Insurance Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="number"
            label="Workmen's Compensation %"
            value={workmensCompensationPct || ''}
            onChange={(e) => setWorkmensCompensationPct(Number(e.target.value) || 0)}
            inputProps={{ step: '0.01', min: 0, max: 100 }}
            disabled={!workmensCompensationRequired}
            fullWidth
            required={workmensCompensationRequired}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} />

        {/* Tax Clearance Certificate */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={taxClearanceCertRequired}
                onChange={(e) => {
                  setTaxClearanceCertRequired(e.target.checked);
                  if (!e.target.checked) setTaxClearanceCertPct(5);
                }}
              />
            }
            label="Tax Clearance Certificate Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="number"
            label="Tax Clearance Certificate %"
            value={taxClearanceCertPct || ''}
            onChange={(e) => setTaxClearanceCertPct(Number(e.target.value) || 0)}
            inputProps={{ step: '0.01', min: 0, max: 100 }}
            disabled={!taxClearanceCertRequired}
            fullWidth
            required={taxClearanceCertRequired}
            helperText={taxClearanceCertRequired ? 'Default: 5%' : ''}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} />

        {/* Advance Payment Guarantee */}
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={advancePaymentGuaranteeRequired}
                onChange={(e) => {
                  setAdvancePaymentGuaranteeRequired(e.target.checked);
                  if (!e.target.checked) setAdvancePaymentPct(0);
                }}
              />
            }
            label="Advance Payment Guarantee Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="number"
            label="Advance Payment %"
            value={advancePaymentPct || ''}
            onChange={(e) => setAdvancePaymentPct(Number(e.target.value) || 0)}
            inputProps={{ step: '0.01', min: 0, max: 100 }}
            disabled={!advancePaymentGuaranteeRequired}
            fullWidth
            required={advancePaymentGuaranteeRequired}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} />

        {/* Retention % */}
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            size="small"
            type="number"
            label="Retention %"
            value={retentionPct || ''}
            onChange={(e) => setRetentionPct(Number(e.target.value) || 0)}
            inputProps={{ step: '0.01', min: 0, max: 100 }}
            fullWidth
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
        </Grid>

        {/* ── Additional Document Requirements ── */}
        <Grid item xs={12}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: '#1a237e', mt: 1 }}>
            Additional Document Requirements
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={<Switch size="small" checked={retentionGuaranteeRequired} onChange={(e) => setRetentionGuaranteeRequired(e.target.checked)} />}
            label="Retention Guarantee Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={<Switch size="small" checked={subcontractAgreementRequired} onChange={(e) => setSubcontractAgreementRequired(e.target.checked)} />}
            label="Subcontract Agreement Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={<Switch size="small" checked={letterOfAuthorizationRequired} onChange={(e) => setLetterOfAuthorizationRequired(e.target.checked)} />}
            label="Letter of Authorization Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={<Switch size="small" checked={civilIDCopiesRequired} onChange={(e) => setCivilIDCopiesRequired(e.target.checked)} />}
            label="Civil ID Copies Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FormControlLabel
            control={<Switch size="small" checked={bankAccountDetailsRequired} onChange={(e) => setBankAccountDetailsRequired(e.target.checked)} />}
            label="Bank Account Details Required"
            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
          />
        </Grid>
      </Grid>

      {/* ===== DETAIL BLOCK - Line Items (PO-style) ===== */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2, mb: 1 }}>
        <Typography sx={sectionHeaderSx}>Subcontract Line Items</Typography>
        <Typography variant="caption" color="text.secondary">
          Auto-generated and read-only
        </Typography>
      </Stack>
      <Divider sx={{ mb: 2 }} />

      <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%', mb: 2 }}>
        <AgGridReact
          ref={lineGridRef}
          rowData={lines}
          columnDefs={lineColumnDefs}
          defaultColDef={{ resizable: true, sortable: true, filter: true }}
          rowSelection={'single' as any}
          rowHeight={32}
          headerHeight={34}
          onCellClicked={onLineCellClicked}
          onCellValueChanged={onLineCellValueChanged}
          onCellDoubleClicked={onLineCellDoubleClicked}
          suppressClickEdit={true}
          readOnlyEdit={true}
          stopEditingWhenCellsLoseFocus={true}
        />
      </Box>

      {/* ===== Summary ===== */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Box sx={{ textAlign: 'right', minWidth: 280 }}>
          <Typography sx={{ fontSize: '0.9rem', color: '#666' }}>
            Lines Total: {linesTotal.toFixed(3)}{subcontractCurrency ? ` ${subcontractCurrency}` : ''}
          </Typography>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 600 }}>
            Subcontract Total: {Number(subcontractValue || 0).toFixed(3)}{subcontractCurrency ? ` ${subcontractCurrency}` : ''}
          </Typography>
        </Box>
      </Box>

      {/* ===== Bottom Action Buttons ===== */}
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          variant="outlined"
          size="small"
          onClick={handleCancel}
          disabled={submitting}
        >
          {savedSubcontractId ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleSave(false)}
          disabled={isReadOnly}
          sx={goldButtonSx}
        >
          {savedSubcontractId ? 'Saved' : 'Save Draft'}
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleSave(true)}
          disabled={isReadOnly}
          sx={goldButtonSx}
        >
          Submit
        </Button>
      </Stack>
      </fieldset>

      {/* ===== Project Picker Dialog ===== */}
      <ProjectChooseDialog
        open={projectPickerOpen}
        projects={projectOptions}
        loading={projectOptionsLoading}
        selectedCode={projectCode}
        onClose={() => setProjectPickerOpen(false)}
        onSelect={handleProjectSelect}
      />

      {/* ===== Item Picker Dialog ===== */}
      <Dialog open={itemPickerOpen} onClose={() => { setItemPickerOpen(false); setItemSearchQuery(''); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Select Item
            <IconButton onClick={() => { setItemPickerOpen(false); setItemSearchQuery(''); }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField size="small" placeholder="Search items..." value={itemSearchQuery} onChange={(e) => setItemSearchQuery(e.target.value)} fullWidth sx={{ mb: 2 }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} /> }} />
          <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%' }}>
            <AgGridReact
              rowData={filteredItemOptions.slice(0, 200)}
              columnDefs={[
                { field: 'ItemCode', headerName: 'Item Code', flex: 1 },
                { field: 'ItemName', headerName: 'Item Name', flex: 2 },
                { field: 'PurchaseUnit', headerName: 'UoM', flex: 0.5 },
                { field: 'Price', headerName: 'Price', flex: 0.5 },
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => handleItemSelect(p.data)}
              onRowDoubleClicked={(p: any) => handleItemSelect(p.data)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* ===== Inline Picker Dialog (Warehouse, Project, Dimensions) ===== */}
      <Dialog open={inlinePickerOpen} onClose={() => { setInlinePickerOpen(false); setInlineSearchQuery(''); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            {inlinePickerTitle}
            <IconButton onClick={() => { setInlinePickerOpen(false); setInlineSearchQuery(''); }}><CloseIcon /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField size="small" placeholder="Search..." value={inlineSearchQuery} onChange={(e) => setInlineSearchQuery(e.target.value)} fullWidth sx={{ mb: 2 }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} /> }} />
          <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%' }}>
            <AgGridReact
              rowData={filteredInlineData.slice(0, 200)}
              columnDefs={inlinePickerColumns}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => chooseInlineValue(p.data)}
              onRowDoubleClicked={(p: any) => chooseInlineValue(p.data)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* ===== Vendor Picker Dialog ===== */}
      <Dialog
        open={vendorPickerOpen}
        onClose={() => { setVendorPickerOpen(false); setVendorSearchQuery(''); }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            Select Vendor / Subcontractor
            <IconButton onClick={() => { setVendorPickerOpen(false); setVendorSearchQuery(''); }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <TextField
            size="small"
            placeholder="Search vendors..."
            value={vendorSearchQuery}
            onChange={(e) => setVendorSearchQuery(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: '#999' }} />,
            }}
          />
          {!vendorSearchQuery.trim() ? (
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Showing the first 100 local vendors. Type to narrow the list.
            </Typography>
          ) : null}
          {vendorOptionsLoading ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, color: 'text.secondary' }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading vendors...</Typography>
            </Stack>
          ) : null}
          <Box className="ag-theme-alpine" sx={{ height: 360, width: '100%' }}>
            <AgGridReact
              rowData={filteredVendorOptions.slice(0, 200)}
              columnDefs={[
                { field: 'CardCode', headerName: 'Vendor Code', flex: 1 },
                { field: 'CardName', headerName: 'Vendor Name', flex: 2 },
              ]}
              defaultColDef={{ resizable: true, sortable: true, filter: true }}
              onRowClicked={(p: any) => handleVendorSelect(p.data)}
              onRowDoubleClicked={(p: any) => handleVendorSelect(p.data)}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default CreateSubcontract;
